/**
 * DietTagFilterBody Component
 *
 * Renders diet tag icon chips (vegan, vegetarian, pescatarian, spicy)
 * inside the FiltersModal body for the Tags tab.
 * Each chip toggles its tag in/out of the selected set.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useTranslation } from '../../../hooks/useTranslation';
import { CHIP_UNSELECTED, CHIP_SELECTED } from './filterChipStyles';

// Icons
import { VeganIcon, VegetarianIcon, PescatarianIcon, SpicyIcon } from '../../icons';
import { ChefRecoIcon } from '../../icons/ChefRecoIcon';

/* ---- Layout constants ---- */
const GRID_CLASSES = 'grid grid-cols-4 gap-4';

/* ---- Chip classes ---- */
const CHIP_BASE = 'flex flex-col items-center justify-center gap-[3px] h-[66px] border rounded-input cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform,opacity] ease-in-out active:scale-95 focus-visible:border-brand-yellow font-inter text-sm font-normal leading-normal text-center';

/* ---- Types ---- */
interface DietTagFilterBodyProps {
  selectedTags: string[];
  onToggle: (tag: string) => void;
  chefPickSelected: boolean;
  onToggleChefPick: () => void;
}

interface DietTag {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string; color?: string }>;
}

/* ---- Data ---- */
// Mirrors PreDefinedFoodTags::Diet::ALL (app/models/concerns/pre_defined_food_tags.rb)
const DIET_TAGS: DietTag[] = [
  { id: 'vegan', label: 'vegan', Icon: VeganIcon },
  { id: 'vegetarian', label: 'vegetarian', Icon: VegetarianIcon },
  { id: 'pescatarian', label: 'pescatarian', Icon: PescatarianIcon },
  { id: 'spicy', label: 'spicy', Icon: SpicyIcon },
];

/* ---- Component ---- */
export function DietTagFilterBody({ selectedTags, onToggle, chefPickSelected, onToggleChefPick }: DietTagFilterBodyProps) {
  const { t } = useTranslation();
  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-inter text-sm font-semibold leading-normal text-grey">
        Dietary and featured
      </span>
      <div className={GRID_CLASSES}>
        {DIET_TAGS.map(({ id, label, Icon }) => {
          const isSelected = selectedSet.has(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className={cn(CHIP_BASE, isSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
              onClick={() => onToggle(id)}
            >
              <Icon className="size-[14px]" color="currentColor" />
              {label}
            </button>
          );
        })}
      </div>
      <div className={GRID_CLASSES}>
        <button
          type="button"
          role="checkbox"
          aria-checked={chefPickSelected}
          className={cn(CHIP_BASE, chefPickSelected ? CHIP_SELECTED : CHIP_UNSELECTED)}
          onClick={onToggleChefPick}
        >
          <ChefRecoIcon className="size-[14px]" color="currentColor" />
          {t('common.fields.chefs_pick')}
        </button>
      </div>
    </div>
  );
}
